import { useCallback, useRef } from 'react';
import {
  HIRING_AUTHORITY_PROFILE_CONFIG,
  PROFILE_REQUESTS
} from 'features/inventory-profiles/constants';
import { buildProfileEndpoint } from 'features/inventory-profiles/utils';
import { useCreateProfileView } from 'hooks/createProfileView';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import API from 'services/API';
import { UIStatus } from 'UI/constants/status';

const INITIAL_HIRING_AUTHORITY = {};

export const useHiringAuthorityProfile = id => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const latestRequestRef = useRef(null);

  const hiringAuthorityEndpoint = buildProfileEndpoint({
    entity: HIRING_AUTHORITY_PROFILE_CONFIG,
    id,
    isEnabled: checkIfFeatureFlagEnabled
  });

  const loadProfile = useCallback(() => {
    const profileRequest = API.get(hiringAuthorityEndpoint, { apiVersion: 2 });
    latestRequestRef.current = profileRequest;

    const resolveWithLatestRequest = () => latestRequestRef.current;

    return profileRequest.then(resolveWithLatestRequest, resolveWithLatestRequest);
  }, [hiringAuthorityEndpoint]);

  const { state, refreshData } = useFetchWithStatusV2({
    promise: id ? loadProfile : undefined,
    initialState: INITIAL_HIRING_AUTHORITY
  });

  const { status, results, responseStatusCode } = state;
  const isLoading = status === UIStatus.Loading;
  const hiringAuthority = isLoading
    ? INITIAL_HIRING_AUTHORITY
    : results ?? INITIAL_HIRING_AUTHORITY;

  useCreateProfileView({
    id: hiringAuthority.id,
    endpoint: HIRING_AUTHORITY_PROFILE_CONFIG.trackingUrl
  });

  return {
    hiringAuthority,
    error:
      status === UIStatus.Error
        ? { request: PROFILE_REQUESTS.Profile, statusCode: responseStatusCode }
        : null,
    isLoading,
    refresh: () => refreshData(false),
    reload: () => refreshData()
  };
};
